import { supabase } from '../supabaseClient';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;

const OAUTH_CALLBACK_URL = `${SUPABASE_URL}/functions/v1/google-oauth-callback`;
const SCOPE = 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.app.created';

/**
 * Builds the URL that starts Google's OAuth consent flow. The `state` param
 * carries the signed-in user's id so the callback function knows whose
 * tokens it's saving; the callback verifies it against the Supabase JWT
 * it's also given.
 */
export async function getGoogleConnectUrl(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return null;
  if (!GOOGLE_CLIENT_ID) {
    throw new Error(
      'VITE_GOOGLE_CLIENT_ID is not set. See README.md "Google Calendar setup".'
    );
  }

  const state = btoa(
    JSON.stringify({ access_token: session.access_token, return_to: window.location.origin })
  );

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: OAUTH_CALLBACK_URL,
    response_type: 'code',
    access_type: 'offline',
    prompt: 'consent',
    scope: SCOPE,
    state,
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export async function isGoogleConnected(): Promise<boolean> {
  const { data, error } = await supabase
    .from('nft_google_calendar_tokens')
    .select('id')
    .maybeSingle();
  if (error) return false;
  return !!data;
}

export async function disconnectGoogle(): Promise<void> {
  await supabase.from('nft_google_calendar_tokens').delete().neq('id', '');
}

async function callEdgeFunction(name: string, body: unknown) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in');

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${name} failed: ${res.status} ${text}`);
  }
  return res.json().catch(() => ({}));
}

export async function syncObligationToCalendar(obligationId: string) {
  if (!(await isGoogleConnected())) return;
  return callEdgeFunction('google-calendar-sync', { obligationId, action: 'upsert' });
}

export async function deleteObligationFromCalendar(obligationId: string) {
  if (!(await isGoogleConnected())) return;
  return callEdgeFunction('google-calendar-sync', { obligationId, action: 'delete' });
}

export async function syncAllObligations() {
  if (!(await isGoogleConnected())) return;
  return callEdgeFunction('google-calendar-sync', { action: 'sync_all' });
}
